import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Bell, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ResendNotificationModal = ({ job, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [fetchingCount, setFetchingCount] = useState(true);
  const [sent, setSent] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  useEffect(() => {
    if (isOpen && job) {
      // Reset state when modal opens for a new job
      setSent(false);
      setSentCount(0);
      setRecipientCount(null);
      setFetchingCount(true);
      fetchRecipientCount();
    }
  }, [isOpen, job?.id]);

  const fetchRecipientCount = async () => {
    setFetchingCount(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/count-job-notification-recipients/${job.id}`
      );
      setRecipientCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch recipient count:', error);
      toast.error('Failed to load recipient count');
      setRecipientCount(0);
    } finally {
      setFetchingCount(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      // Resend notifications by calling the job notification service directly
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/resend-job-notifications`,
        { job_id: job.id }
      );

      if (response.data.success) {
        setSent(true);
        setSentCount(response.data.notifications_sent);
        toast.success(`Sent notifications to ${response.data.notifications_sent} users`);
        
        // Close after 2 seconds
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to resend notifications:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSent(false);
      setSentCount(0);
      setRecipientCount(null);
      onClose();
    }
  };

  if (!isOpen || !job) return null;

  const stop = (e) => e.stopPropagation();

  return (
    <div className="sz-modal-backdrop" style={{ zIndex: 200 }} onClick={handleClose}>
      <div className="sz-modal-card w-full max-w-[440px] overflow-hidden" onClick={stop}>
        {/* Gradient header */}
        <div className="sz-modal-header-grad p-[20px_24px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center" style={{ background: "rgba(255,255,255,.18)" }}>
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="sz-h3 text-white" style={{ fontSize: 17 }}>Resend Job Notifications</h3>
              <div className="text-[12px] mt-0.5 truncate max-w-[280px]" style={{ color: "rgba(255,255,255,.82)" }}>{job.title}</div>
            </div>
          </div>
          {!loading && !sent && (
            <button onClick={handleClose} className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-white shrink-0" style={{ background: "rgba(255,255,255,.18)" }}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-[20px_24px]">
          {!sent ? (
            <>
              {/* Recipient count */}
              <div className="rounded-[12px] p-4 mb-4" style={{ background: "#EDE4F4", border: "1px solid #DDD0EA" }}>
                {fetchingCount ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-[#572B7C]" />
                    <span className="text-[#572B7C]">Calculating recipients…</span>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#572B7C] mb-1" style={{ fontFamily: "Poppins" }}>{recipientCount}</p>
                    <p className="text-sm text-[#572B7C]">{recipientCount === 1 ? "user will be notified" : "users will be notified"}</p>
                  </div>
                )}
              </div>

              {recipientCount > 0 && (
                <div className="rounded-[11px] p-3 mb-4 flex gap-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                  <AlertCircle className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-[#92400E] font-semibold mb-1">Confirmation Required</p>
                    <p className="text-xs text-[#B45309]">This will send notification emails about this job to all eligible users within their notification radius.</p>
                  </div>
                </div>
              )}

              {recipientCount === 0 && !fetchingCount && (
                <div className="rounded-[11px] p-3 mb-4 flex gap-3" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>
                  <AlertCircle className="w-5 h-5 text-[#8A90A2] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-[#2B3142]">No eligible users found for this job. This could be because:</p>
                    <ul className="text-xs text-[#8A90A2] mt-2 space-y-1 list-disc list-inside">
                      <li>No users within notification radius</li>
                      <li>Users have disabled job notifications</li>
                      <li>Job location not geocoded</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex gap-2.5">
                <button onClick={handleClose} disabled={loading} className="sz-btn sz-btn-outline flex-1">Cancel</button>
                <button onClick={handleResend} disabled={loading || fetchingCount || recipientCount === 0} className="sz-btn sz-btn-primary flex-1">
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending…</>) : "Send Notifications"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#15803D" }}>
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#0C1120] text-center mb-2" style={{ fontFamily: "Poppins" }}>Notifications Sent!</h3>
              <p className="text-[#8A90A2] text-center mb-4">Successfully sent notifications to <strong className="text-[#0C1120]">{sentCount}</strong> {sentCount === 1 ? "user" : "users"}</p>
              <div className="rounded-[11px] p-4 text-center" style={{ background: "#DCFCE7", border: "1px solid #BBF7D0" }}>
                <p className="text-sm text-[#166534]">Users will receive emails with job details and can apply directly.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResendNotificationModal;
